import React from "react";
import { useGetConversationsQuery, useToggleConversationPinMutation } from "../../../../store/slices/api";
import { SharedLoading, SharedFeatureError } from "../../../shared";
import LetterRow from "./LetterRow";

function Inbox({ individualId, filter, onOpenConversation }) {
  const { data: conversations, isLoading, isError, refetch } =
    useGetConversationsQuery(individualId, {
      skip: !individualId,
      pollingInterval: 10000,
    });
  const [togglePin] = useToggleConversationPinMutation();

  const handleTogglePin = (conversationId) => {
    togglePin({ conversationId, individual_id: individualId });
  };

  if (isLoading) {
    return <SharedLoading />;
  }

  // Show a friendly retry on error — never the misleading "tray is empty",
  // which reads to a worried relative like their letters were lost.
  if (isError) {
    return <SharedFeatureError onRetry={refetch} />;
  }

  if (!conversations || conversations.length === 0) {
    return (
      <div className="correspondence-empty">
        {filter === "sent"
          ? "You haven't sent any letters yet."
          : "Your correspondence tray is empty."}
      </div>
    );
  }

  // Filter conversations based on the tab:
  // "inbox" = conversations where the last message was NOT from you
  // "sent"  = conversations where YOU sent the most recent message
  const filtered = conversations.filter((conv) => {
    if (!conv.last_message) return filter === "inbox";
    const lastSentByMe =
      conv.last_message.sender_individual_id === individualId;
    return filter === "sent" ? lastSentByMe : !lastSentByMe;
  });

  // If filtering leaves nothing, still show all on inbox tab
  const displayList =
    filter === "inbox" && filtered.length === 0 ? conversations : filtered;

  if (displayList.length === 0) {
    return (
      <div className="correspondence-empty">
        {filter === "sent"
          ? "You haven't sent any letters yet."
          : "No new letters to read."}
      </div>
    );
  }

  // Split into pinned (Favorites) and unpinned (Library)
  const pinned = displayList.filter((conv) => conv.is_pinned);
  const unpinned = displayList.filter((conv) => !conv.is_pinned);

  return (
    <div className="flex flex-col gap-6">
      {/* Favorites section — only shows when there are pinned conversations */}
      {pinned.length > 0 && (
        <div className="rounded-2xl border border-dupontGold/30 dark:border-dupontGold/20 bg-white dark:bg-darkCard shadow-md overflow-hidden">
          <div className="px-5 py-3 border-b border-dupontGold/20 bg-dupontCream dark:bg-darkPrimary flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-dupontGold">
              <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z" clipRule="evenodd" />
            </svg>
            <span className="text-dupontGold font-display font-semibold text-[16px]">Favorites</span>
          </div>
          <div className="flex flex-col">
            {pinned.map((conversation, index) => (
              <div key={conversation.id}>
                <LetterRow
                  conversation={conversation}
                  individualId={individualId}
                  onClick={() => onOpenConversation(conversation.id)}
                  isPinned={true}
                  onTogglePin={handleTogglePin}
                />
                {index < pinned.length - 1 && (
                  <div className="correspondence-divider" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Correspondence Library — always shows */}
      <div className="rounded-2xl border border-dupontGold/30 dark:border-dupontGold/20 bg-white dark:bg-darkCard shadow-md overflow-hidden">
        <div className="px-5 py-3 border-b border-dupontGold/20 bg-dupontCream dark:bg-darkPrimary">
          <span className="text-dupontBlue dark:text-darkText font-display font-semibold text-[16px]">Correspondence Library</span>
        </div>
        <div className="flex flex-col">
          {unpinned.length > 0 ? (
            unpinned.map((conversation, index) => (
              <div key={conversation.id}>
                <LetterRow
                  conversation={conversation}
                  individualId={individualId}
                  onClick={() => onOpenConversation(conversation.id)}
                  isPinned={false}
                  onTogglePin={handleTogglePin}
                />
                {index < unpinned.length - 1 && (
                  <div className="correspondence-divider" />
                )}
              </div>
            ))
          ) : (
            <div className="px-5 py-6 text-center text-dupontGray dark:text-darkTextSecondary font-body text-[14px] italic">
              All conversations are in your Favorites
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Inbox;
